// Drawings for the first-time setup: plain shapes, large labels, no brand artwork.

/** Chrome's question, small, under the address bar at the top-left. */
export function ChromeAllowPicture() {
  return (
    <svg viewBox="0 0 520 250" className="setup-picture" role="img"
      aria-label="A browser window: under the address bar, at the left, a small box asks to manage windows on all your displays, with an Allow button">
      <rect x="4" y="4" width="512" height="242" rx="10" fill="#fff" stroke="#9aa5b1" strokeWidth="2" />
      <rect x="4" y="4" width="512" height="34" rx="10" fill="#e8ecf1" />
      <circle cx="24" cy="21" r="6" fill="#e06c5f" /><circle cx="42" cy="21" r="6" fill="#e3b341" /><circle cx="60" cy="21" r="6" fill="#5cb85c" />
      <rect x="84" y="46" width="410" height="24" rx="12" fill="#f1f3f6" stroke="#c8d0d9" />
      <text x="100" y="63" fontSize="13" fill="#56616c">the study's address</text>
      <rect x="22" y="80" width="300" height="122" rx="8" fill="#fff" stroke="#5b6b7b" strokeWidth="1.5" />
      <text x="38" y="106" fontSize="14" fill="#1f2a35">This site wants to</text>
      <text x="38" y="128" fontSize="14" fontWeight="bold" fill="#1f2a35">Manage windows on all your displays</text>
      <rect x="132" y="150" width="80" height="34" rx="17" fill="#f1f3f6" stroke="#c8d0d9" />
      <text x="150" y="172" fontSize="14" fill="#1f2a35">Block</text>
      <rect x="222" y="150" width="84" height="34" rx="17" fill="#1a73e8" />
      <text x="246" y="172" fontSize="14" fill="#fff" fontWeight="bold">Allow</text>
      <circle cx="264" cy="167" r="30" fill="none" stroke="#d93025" strokeWidth="4" />
      <path d="M 350 205 L 300 182" stroke="#d93025" strokeWidth="4" markerEnd="url(#arr)" />
      <text x="356" y="216" fontSize="16" fontWeight="bold" fill="#d93025">click Allow</text>
      <defs><marker id="arr" markerWidth="10" markerHeight="10" refX="6" refY="5" orient="auto">
        <path d="M0,0 L10,5 L0,10 z" fill="#d93025" /></marker></defs>
    </svg>
  );
}

/** The measuring cord from the screen to the eyes. */
export function CordPicture({ metres }: { metres: number }) {
  return (
    <svg viewBox="0 0 520 200" className="setup-picture" role="img"
      aria-label={`A person sitting in front of a monitor; a cord ${metres} metres long stretches from the screen to their eyes`}>
      <rect x="20" y="40" width="22" height="110" rx="3" fill="#2d3b48" />
      <rect x="42" y="38" width="8" height="114" fill="#cfe3f5" />
      <rect x="10" y="150" width="44" height="8" rx="3" fill="#2d3b48" />
      <rect x="27" y="150" width="8" height="30" fill="#2d3b48" />
      <circle cx="440" cy="82" r="30" fill="#f2d4b8" stroke="#8a6a50" strokeWidth="2" />
      <circle cx="420" cy="78" r="4" fill="#1f2a35" />
      <path d="M 470 60 Q 486 82 470 108" fill="none" stroke="#8a6a50" strokeWidth="2" />
      <path d="M 400 120 Q 440 112 480 120 L 500 196 L 380 196 Z" fill="#7e9cbc" />
      <path d="M 50 80 C 160 96, 300 96, 416 80" fill="none" stroke="#d97706" strokeWidth="4" strokeDasharray="10 6" />
      <circle cx="50" cy="80" r="5" fill="#d97706" /><circle cx="416" cy="80" r="5" fill="#d97706" />
      <text x="190" y="72" fontSize="20" fontWeight="bold" fill="#b45309">{metres} m cord</text>
      <text x="150" y="130" fontSize="15" fill="#56616c">screen  →  your eyes, cord just straight</text>
    </svg>
  );
}

/** The four arrow keys (or 8 / 4 / 6 / 2 on the number pad). */
export function KeypadPicture({ lit }: { lit: Record<string, boolean> }) {
  const key = (x: number, y: number, k: string, label: string) => (
    <g key={k}>
      <rect x={x} y={y} width="70" height="70" rx="10" fill={lit[k] ? "#2e7d32" : "#f1f3f6"} stroke="#5b6b7b" strokeWidth="2" />
      <text x={x + 35} y={y + 47} fontSize="34" textAnchor="middle" fill={lit[k] ? "#fff" : "#1f2a35"}>{label}</text>
    </g>
  );
  return (
    <svg viewBox="0 0 250 165" className="setup-picture keypad-picture" role="img"
      aria-label="The four arrow keys; each one turns green when you press it">
      {key(90, 5, "up", "↑")}
      {key(10, 85, "left", "←")}{key(90, 85, "down", "↓")}{key(170, 85, "right", "→")}
    </svg>
  );
}

/** "Check my monitor": the computer reads the monitor's own identity over the cable. */
export function HelperPicture() {
  return (
    <svg viewBox="0 0 520 170" className="setup-picture" role="img"
      aria-label="A laptop connected by a cable to a monitor; the download reads the monitor's identity (maker, model, serial, size) and sends it to the study">
      <rect x="20" y="40" width="150" height="95" rx="6" fill="#2d3b48" />
      <rect x="28" y="48" width="134" height="79" fill="#e8f1fb" />
      <rect x="6" y="135" width="178" height="12" rx="4" fill="#5b6b7b" />
      <text x="95" y="92" fontSize="13" textAnchor="middle" fill="#1f2a35">Check my monitor</text>
      <path d="M 184 140 C 250 150, 290 150, 340 120" fill="none" stroke="#5b6b7b" strokeWidth="5" />
      <rect x="330" y="20" width="170" height="110" rx="6" fill="#2d3b48" />
      <rect x="338" y="28" width="154" height="94" fill="#f7f9fb" />
      <rect x="405" y="130" width="20" height="22" fill="#2d3b48" /><rect x="380" y="150" width="70" height="8" rx="3" fill="#2d3b48" />
      <text x="415" y="58" fontSize="13" textAnchor="middle" fill="#1f2a35">maker · model</text>
      <text x="415" y="80" fontSize="13" textAnchor="middle" fill="#1f2a35">serial number</text>
      <text x="415" y="102" fontSize="13" textAnchor="middle" fill="#1f2a35">size · colour bits</text>
    </svg>
  );
}
